import { useState, type FormEvent } from 'react';
import { api, type ControlSummary, type Finding, type RiskAcceptance } from '../api.ts';
import { message } from './format.ts';

/**
 * The risks this client has decided to live with.
 *
 * It is a register, not a filter. Accepting a control changes nothing about its
 * verdict — the report still says Not met, and says who accepted it and why —
 * and the one thing it does change is the alert channel, which stops repeating
 * a failure somebody has already answered.
 *
 * Two things this screen refuses to make easy, because both are the failure
 * this feature could become. It will not accept a control that is not failing,
 * and it will not take a review date in the past: an acceptance with no end is
 * silence, and silence is what an auditor asks about first.
 */

interface AcceptedRisksProps {
  readonly tenantId: string;
  readonly acceptances: RiskAcceptance[];
  readonly findings: Finding[] | null;
  readonly controls: ControlSummary[];
  readonly canAccept: boolean;
  readonly onChanged: () => void;
  readonly onError: (text: string) => void;
}

const formatDay = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const today = (): string => new Date().toISOString().slice(0, 10);

export const AcceptedRisks = ({
  tenantId,
  acceptances,
  findings,
  controls,
  canAccept,
  onChanged,
  onError,
}: AcceptedRisksProps) => {
  const [controlId, setControlId] = useState('');
  const [acceptedBy, setAcceptedBy] = useState('');
  const [reason, setReason] = useState('');
  const [reviewBy, setReviewBy] = useState('');
  const [busy, setBusy] = useState(false);

  const titleOf = (id: string): string => controls.find((c) => c.id === id)?.title ?? id;
  const accepted = new Set(acceptances.map((entry) => entry.controlId));

  /** Only what is failing now, and not already accepted. */
  const acceptable = (findings ?? [])
    .filter((finding) => finding.verdict === 'FAIL' && !accepted.has(finding.controlId))
    .map((finding) => finding.controlId)
    .sort();

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.acceptRisk(tenantId, controlId, { acceptedBy, reason, reviewBy });
      setControlId('');
      setAcceptedBy('');
      setReason('');
      setReviewBy('');
      onChanged();
    } catch (cause) {
      onError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: string) => {
    setBusy(true);
    try {
      await api.withdrawRiskAcceptance(tenantId, id);
      onChanged();
    } catch (cause) {
      onError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="td-stack">
      <p className="lead td-lead">
        A control this client has looked at and decided to live with — mail forwarded to a
        ticketing system, a sharing default the business runs on. The verdict does not change: the
        report still reports it as not met, beside who accepted it and why. What changes is that it
        stops raising an alert every assessment, until the review date passes or the finding grows
        beyond what was agreed.
      </p>

      {acceptances.length === 0 ? (
        <div className="empty">
          <span className="empty-title">Nothing accepted</span>
          <p className="empty-body">
            Every failing control is outstanding. That is the right state for most clients.
          </p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Control</th>
                <th>Accepted by</th>
                <th>Reason</th>
                <th>Review due</th>
                {canAccept && <th />}
              </tr>
            </thead>
            <tbody>
              {acceptances.map((entry) => (
                <tr key={entry.controlId}>
                  <td>
                    <span className="mono">{entry.controlId}</span> {titleOf(entry.controlId)}
                  </td>
                  <td>
                    {entry.acceptedBy}
                    <div className="dw-void">
                      recorded by {entry.recordedBy}, {formatDay(entry.acceptedAt)}
                    </div>
                  </td>
                  <td>{entry.reason}</td>
                  <td className={entry.reviewBy < today() ? 'ar-overdue' : undefined}>
                    {entry.reviewBy}
                    {entry.reviewBy < today() ? ' — overdue' : ''}
                  </td>
                  {canAccept && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy}
                        onClick={() => void withdraw(entry.controlId)}
                      >
                        Withdraw
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canAccept && (
        <form className="card ar-form" onSubmit={(event) => void submit(event)}>
          <div className="card-body">
            <h3>Record an acceptance</h3>
            {acceptable.length === 0 ? (
              <p className="note">
                No control is currently failing and unaccepted. Only a failing control can be
                accepted — there is nothing to decide about one that passes.
              </p>
            ) : (
              <>
                <label className="field">
                  <span className="label">Control</span>
                  <select value={controlId} onChange={(e) => setControlId(e.target.value)} required>
                    <option value="">Choose a failing control</option>
                    {acceptable.map((id) => (
                      <option key={id} value={id}>
                        {id} — {titleOf(id)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="label">Accepted by</span>
                  <input
                    type="text"
                    value={acceptedBy}
                    maxLength={200}
                    required
                    placeholder="The person at the client who decided"
                    onChange={(e) => setAcceptedBy(e.target.value)}
                  />
                </label>

                <label className="field">
                  <span className="label">Reason</span>
                  <textarea
                    value={reason}
                    maxLength={2000}
                    required
                    rows={3}
                    placeholder="In the client’s words. An auditor reads this."
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>

                <label className="field">
                  <span className="label">Review by</span>
                  <input
                    type="date"
                    value={reviewBy}
                    min={today()}
                    required
                    onChange={(e) => setReviewBy(e.target.value)}
                  />
                </label>

                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Record acceptance'}
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </section>
  );
};
